import { useEffect, useMemo, useState } from "react";
import { getAddress, type Address, type Hash } from "viem";
import { baseClient, ethereumClient } from "./chain";
import {
  BURN_TOKEN,
  HARDWARE_COLLECTION,
  HARDWARE_METADATA_BASE_URI,
  LEGACY_MINTER,
  hardwareCollectionAbi,
  receiverAbi,
} from "./contracts";
import { loadDeploymentConfig, saveDeploymentConfig, type DeploymentConfig } from "./deployment";
import { hardwareBurnerArtifact, hardwareReceiverArtifact } from "./generated/contractArtifacts";
import {
  baseWallet,
  connectInjectedWallet,
  ethereumWallet,
  friendlyError,
  switchToBase,
  switchToEthereum,
} from "./wallet";

type Status = {
  kind: "idle" | "pending" | "success" | "error";
  message?: string;
  hash?: Hash;
  chain?: "base" | "ethereum";
};

const LEGACY_RECOVERIES = [
  {
    redemptionNumber: 230n,
    recipient: "0xa75c93E895DC2Fb3B034bf4AC59cf85c4C99aE1f" as Address,
    burnTx: "0x07d031ffb4acd7edc29c6d8c71bf20194e4dae49663bc2f3aa6e98a3935e58be",
  },
  {
    redemptionNumber: 231n,
    recipient: "0xb120839bfB092Cef28315e6c35a0797E1952D535" as Address,
    burnTx: "0x3e0e8c9c04f18231e30365e59baa02760cc69e8b6e6e3ff9b9895aaba46b31ec",
  },
] as const;

const LEGACY_REDEMPTION_COUNT = 231n;
const TOKEN_229_IMAGE = "https://arweave.net/VVC1zQDZ_n8FiJAB4ldx3TQAyUdZ9_nBrFZJz-UFhDY/images/HARDWARE_JB_00117.png";

export function DeployApp() {
  const [account, setAccount] = useState<Address>();
  const [collectionOwner, setCollectionOwner] = useState<Address>();
  const [minters, setMinters] = useState<readonly Address[]>([]);
  const [collectionLoaded, setCollectionLoaded] = useState(false);
  const [config, setConfig] = useState<DeploymentConfig>(() => loadDeploymentConfig());
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [legacyRecovered, setLegacyRecovered] = useState<Record<number, boolean>>({ 230: false, 231: false });
  const [metadataMigrated, setMetadataMigrated] = useState(false);
  const [metadataArchiveReady, setMetadataArchiveReady] = useState(false);

  const shortAccount = useMemo(
    () => account ? shorten(account) : "Connect Ledger wallet",
    [account],
  );
  const ownerMatches = Boolean(account && collectionOwner && account.toLowerCase() === collectionOwner.toLowerCase());
  const receiverRegistered = Boolean(
    config.receiverAddress && minters.some((minter) => minter.toLowerCase() === config.receiverAddress!.toLowerCase()),
  );
  const soleReceiver = receiverRegistered && minters.length === 1;
  const recoveryComplete = legacyRecovered[230] && legacyRecovered[231];
  const legacyMinterRegistered = collectionLoaded
    && minters.some((minter) => minter.toLowerCase() === LEGACY_MINTER.toLowerCase());
  const legacyMinterRetired = collectionLoaded && !legacyMinterRegistered;
  const complete = Boolean(
    config.burnerAddress
    && config.receiverAddress
    && soleReceiver
    && recoveryComplete
    && metadataMigrated
    && legacyMinterRetired,
  );

  useEffect(() => {
    refreshCollection().catch(() => undefined);
    refreshRecoveryState().catch(() => undefined);
    validateSavedConfig().catch(() => undefined);
    if (!window.ethereum) return;
    window.ethereum.request({ method: "eth_accounts" }).then((accounts) => {
      const first = (accounts as string[])[0];
      if (!first) return;
      const address = getAddress(first);
      setAccount(address);
    });
  }, []);

  async function refreshCollection() {
    const [nextOwner, nextMinters] = await Promise.all([
      ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "owner" }),
      ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "minters" }),
    ]);
    setCollectionOwner(nextOwner);
    setMinters(nextMinters);
    setCollectionLoaded(true);
  }

  async function refreshRecoveryState(receiverAddress = config.receiverAddress) {
    const baseUriPromise = ethereumClient.readContract({
      address: HARDWARE_COLLECTION,
      abi: hardwareCollectionAbi,
      functionName: "baseURI",
    });
    const recoveryPromise = receiverAddress
      ? Promise.all(LEGACY_RECOVERIES.map((recovery) => ethereumClient.readContract({
          address: receiverAddress,
          abi: receiverAbi,
          functionName: "legacyRecovered",
          args: [recovery.redemptionNumber],
        })))
      : Promise.resolve([false, false]);
    const [baseUri, recoveryState] = await Promise.all([baseUriPromise, recoveryPromise]);
    setMetadataMigrated(baseUri.replace(/\/$/, "") === HARDWARE_METADATA_BASE_URI);
    setLegacyRecovered({ 230: recoveryState[0], 231: recoveryState[1] });
    if (recoveryState[0] && recoveryState[1]) {
      verifyMetadataArchive([1, 229, 230, 231]).then(() => setMetadataArchiveReady(true)).catch(() => setMetadataArchiveReady(false));
    } else {
      setMetadataArchiveReady(false);
    }
  }

  async function verifyMetadataArchive(tokenIds: readonly number[]) {
    await Promise.all(tokenIds.map(async (tokenId) => {
      const response = await fetch(`/metadata/${tokenId}`, { cache: "no-store" });
      if (!response.ok) throw new Error(`The independent metadata endpoint for Hardware #${tokenId} is not ready.`);
      const metadata = await response.json() as { name?: string; image?: string; animation_url?: string };
      if (
        metadata.name !== `Hardware #${tokenId}`
        || !metadata.image?.startsWith("https://arweave.net/")
        || !metadata.animation_url?.startsWith("https://arweave.net/")
      ) throw new Error(`The independent metadata endpoint for Hardware #${tokenId} returned an unexpected record.`);
    }));
  }

  async function validateSavedConfig() {
    const current = loadDeploymentConfig();
    const currentOwner = await ethereumClient.readContract({
      address: HARDWARE_COLLECTION,
      abi: hardwareCollectionAbi,
      functionName: "owner",
    });
    let next = current;
    if (current.burnerAddress) {
      try {
        const [burnerOwner, burnToken, burnTokenId, redemptionCount, deploymentBlock, maximumRedemption] = await Promise.all([
          baseClient.readContract({ address: current.burnerAddress, abi: hardwareBurnerArtifact.abi, functionName: "owner" }),
          baseClient.readContract({ address: current.burnerAddress, abi: hardwareBurnerArtifact.abi, functionName: "burnToken" }),
          baseClient.readContract({ address: current.burnerAddress, abi: hardwareBurnerArtifact.abi, functionName: "burnTokenId" }),
          baseClient.readContract({ address: current.burnerAddress, abi: hardwareBurnerArtifact.abi, functionName: "redemptionCount" }),
          baseClient.readContract({ address: current.burnerAddress, abi: hardwareBurnerArtifact.abi, functionName: "deploymentBlock" }),
          baseClient.readContract({ address: current.burnerAddress, abi: hardwareBurnerArtifact.abi, functionName: "MAX_REDEMPTION_NUMBER" }),
        ]);
        if (
          burnerOwner.toLowerCase() !== currentOwner.toLowerCase()
          || burnToken.toLowerCase() !== BURN_TOKEN.toLowerCase()
          || burnTokenId !== 1n
          || redemptionCount < LEGACY_REDEMPTION_COUNT
          || maximumRedemption !== 544n
        ) throw new Error("Invalid saved burner.");
        next = { ...next, burnerDeploymentBlock: deploymentBlock.toString() };
      } catch {
        next = { ...next, burnerAddress: undefined, burnerDeploymentBlock: undefined, receiverAddress: undefined, receiverDeploymentBlock: undefined };
      }
    }
    if (next.receiverAddress && next.burnerAddress) {
      try {
        const [receiverOwner, hardware, sourceChainId, sourceContract, maximumRedemption] = await Promise.all([
          ethereumClient.readContract({ address: next.receiverAddress, abi: receiverAbi, functionName: "owner" }),
          ethereumClient.readContract({ address: next.receiverAddress, abi: receiverAbi, functionName: "hardware" }),
          ethereumClient.readContract({ address: next.receiverAddress, abi: receiverAbi, functionName: "sourceChainId" }),
          ethereumClient.readContract({ address: next.receiverAddress, abi: receiverAbi, functionName: "sourceContract" }),
          ethereumClient.readContract({ address: next.receiverAddress, abi: receiverAbi, functionName: "MAX_REDEMPTION_NUMBER" }),
        ]);
        if (
          receiverOwner.toLowerCase() !== currentOwner.toLowerCase()
          || hardware.toLowerCase() !== HARDWARE_COLLECTION.toLowerCase()
          || sourceChainId !== 8453n
          || sourceContract.toLowerCase() !== next.burnerAddress.toLowerCase()
          || maximumRedemption !== 544n
        ) throw new Error("Invalid saved receiver.");
      } catch {
        next = { ...next, receiverAddress: undefined, receiverDeploymentBlock: undefined };
      }
    } else if (next.receiverAddress) {
      next = { ...next, receiverAddress: undefined, receiverDeploymentBlock: undefined };
    }
    setConfig(next);
    saveDeploymentConfig(next);
  }

  async function connect() {
    try {
      const address = await connectInjectedWallet();
      setAccount(address);
      await refreshCollection();
      await refreshRecoveryState();
      setStatus({ kind: "success", message: "Ledger-backed wallet connected. Verify every address and chain on the device." });
      return address;
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
      throw error;
    }
  }

  async function requireAccount() {
    return account ?? connect();
  }

  async function requireCollectionOwner(walletAccount: Address) {
    const currentOwner = await ethereumClient.readContract({
      address: HARDWARE_COLLECTION,
      abi: hardwareCollectionAbi,
      functionName: "owner",
    });
    setCollectionOwner(currentOwner);
    if (walletAccount.toLowerCase() !== currentOwner.toLowerCase()) {
      throw new Error(`Connect the current Hardware collection owner: ${currentOwner}.`);
    }
    return currentOwner;
  }

  function commitConfig(next: DeploymentConfig) {
    setConfig(next);
    saveDeploymentConfig(next);
  }

  async function deployBurner() {
    try {
      const walletAccount = await requireAccount();
      const ownerAddress = await requireCollectionOwner(walletAccount);
      if (config.burnerAddress) throw new Error("The Base burner is already deployed in this recovery record.");
      await switchToBase();
      setStatus({ kind: "pending", message: "Review the Hardware burner deployment on Base using your Ledger.", chain: "base" });
      const hash = await baseWallet(walletAccount).deployContract({
        abi: hardwareBurnerArtifact.abi,
        bytecode: hardwareBurnerArtifact.bytecode,
        args: [ownerAddress, BURN_TOKEN, 1n, LEGACY_REDEMPTION_COUNT],
      });
      setStatus({ kind: "pending", message: "Base deployment submitted. Waiting for confirmation…", hash, chain: "base" });
      const receipt = await baseClient.waitForTransactionReceipt({ hash });
      if (!receipt.contractAddress) throw new Error("The Base receipt did not contain a contract address.");
      commitConfig({
        ...config,
        burnerAddress: receipt.contractAddress,
        burnerDeploymentBlock: receipt.blockNumber.toString(),
      });
      setStatus({ kind: "success", message: `Base burner deployed at ${receipt.contractAddress}.`, hash, chain: "base" });
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  async function deployReceiver() {
    try {
      const walletAccount = await requireAccount();
      const ownerAddress = await requireCollectionOwner(walletAccount);
      if (config.receiverAddress) throw new Error("The Ethereum receiver is already deployed in this recovery record.");
      if (!config.burnerAddress) throw new Error("Deploy the Base burner first.");
      await switchToEthereum();
      setStatus({ kind: "pending", message: "Review the Hardware receiver deployment on Ethereum using your Ledger.", chain: "ethereum" });
      const hash = await ethereumWallet(walletAccount).deployContract({
        abi: hardwareReceiverArtifact.abi,
        bytecode: hardwareReceiverArtifact.bytecode,
        args: [ownerAddress, HARDWARE_COLLECTION, 8453n, config.burnerAddress],
      });
      setStatus({ kind: "pending", message: "Ethereum deployment submitted. Waiting for confirmation…", hash, chain: "ethereum" });
      const receipt = await ethereumClient.waitForTransactionReceipt({ hash });
      if (!receipt.contractAddress) throw new Error("The Ethereum receipt did not contain a contract address.");
      commitConfig({
        ...config,
        receiverAddress: receipt.contractAddress,
        receiverDeploymentBlock: receipt.blockNumber.toString(),
      });
      setLegacyRecovered({ 230: false, 231: false });
      setStatus({ kind: "success", message: `Ethereum receiver deployed at ${receipt.contractAddress}.`, hash, chain: "ethereum" });
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  async function registerMinter() {
    if (!config.receiverAddress) {
      setStatus({ kind: "error", message: "Deploy the Ethereum receiver first." });
      return;
    }
    try {
      const walletAccount = await requireAccount();
      await requireCollectionOwner(walletAccount);
      await verifyMetadataArchive([1, 229]);
      await switchToEthereum();
      setStatus({ kind: "pending", message: "Review the receiver minter registration on your Ledger.", chain: "ethereum" });
      const hash = await ethereumWallet(walletAccount).writeContract({
        address: HARDWARE_COLLECTION,
        abi: hardwareCollectionAbi,
        functionName: "registerMinter",
        args: [config.receiverAddress],
      });
      setStatus({ kind: "pending", message: "Minter registration submitted. Waiting for confirmation…", hash, chain: "ethereum" });
      await ethereumClient.waitForTransactionReceipt({ hash });
      await refreshCollection();
      setStatus({ kind: "success", message: "Receiver registered for the two recoveries. Finish Steps 04–06 before public claims activate.", hash, chain: "ethereum" });
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  async function recoverLegacy(redemptionNumber: 230 | 231) {
    if (!config.receiverAddress) return;
    const recovery = LEGACY_RECOVERIES.find((item) => item.redemptionNumber === BigInt(redemptionNumber));
    if (!recovery) return;
    try {
      const walletAccount = await requireAccount();
      await requireCollectionOwner(walletAccount);
      if (!receiverRegistered) throw new Error("Register the receiver as a collection minter before recovering legacy claims.");
      if (redemptionNumber === 231 && !legacyRecovered[230]) throw new Error("Recover #230 first so the token numbers cannot be swapped.");
      await switchToEthereum();
      setStatus({ kind: "pending", message: `Review the one-time recovery for #${redemptionNumber} to ${recovery.recipient} on your Ledger.`, chain: "ethereum" });
      const hash = await ethereumWallet(walletAccount).writeContract({
        address: config.receiverAddress,
        abi: receiverAbi,
        functionName: "recoverLegacyRedemption",
        args: [BigInt(redemptionNumber)],
      });
      setStatus({ kind: "pending", message: `Recovery #${redemptionNumber} submitted. Waiting for confirmation…`, hash, chain: "ethereum" });
      await ethereumClient.waitForTransactionReceipt({ hash });
      await refreshRecoveryState();
      setStatus({ kind: "success", message: `Hardware #${redemptionNumber} minted to the verified legacy burner.`, hash, chain: "ethereum" });
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  async function migrateMetadata() {
    try {
      const walletAccount = await requireAccount();
      await requireCollectionOwner(walletAccount);
      if (!recoveryComplete) throw new Error("Recover Hardware #230 and #231 before moving the collection metadata.");
      await verifyMetadataArchive([1, 229, 230, 231]);
      setMetadataArchiveReady(true);
      await switchToEthereum();
      setStatus({ kind: "pending", message: "Review the Hardware metadata cutover from Highlight to hardware.jack.art on your Ledger.", chain: "ethereum" });
      const hash = await ethereumWallet(walletAccount).writeContract({
        address: HARDWARE_COLLECTION,
        abi: hardwareCollectionAbi,
        functionName: "setBaseURI",
        args: [HARDWARE_METADATA_BASE_URI],
      });
      setStatus({ kind: "pending", message: "Metadata cutover submitted. Waiting for confirmation…", hash, chain: "ethereum" });
      await ethereumClient.waitForTransactionReceipt({ hash });
      await refreshRecoveryState();
      setStatus({ kind: "success", message: "All Hardware token metadata now resolves through hardware.jack.art to permanent Arweave artwork.", hash, chain: "ethereum" });
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  async function retireLegacyMinter() {
    try {
      const walletAccount = await requireAccount();
      await requireCollectionOwner(walletAccount);
      if (!receiverRegistered || !recoveryComplete || !metadataMigrated) {
        throw new Error("Complete the receiver activation and historical repairs first.");
      }
      await switchToEthereum();
      setStatus({ kind: "pending", message: "Review the permanent Highlight minter removal on your Ledger.", chain: "ethereum" });
      const hash = await ethereumWallet(walletAccount).writeContract({
        address: HARDWARE_COLLECTION,
        abi: hardwareCollectionAbi,
        functionName: "unregisterMinter",
        args: [LEGACY_MINTER],
      });
      setStatus({ kind: "pending", message: "Highlight minter removal submitted. Waiting for confirmation…", hash, chain: "ethereum" });
      await ethereumClient.waitForTransactionReceipt({ hash });
      await refreshCollection();
      setStatus({ kind: "success", message: "Highlight’s old minter is no longer authorized.", hash, chain: "ethereum" });
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  function exportConfig() {
    const body = JSON.stringify(config, null, 2);
    const url = URL.createObjectURL(new Blob([`${body}\n`], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "hardware-runtime-config.json";
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="deploy-shell">
      <header className="deploy-header">
        <div>
          <a className="back-link" href="/admin">← Owner console</a>
          <p className="eyebrow">Hardware / contract deployment</p>
          <h1>Ledger signed.<br />No hot keys.</h1>
          <p className="deploy-intro">Deploy the independent redemption contracts directly from a Ledger-backed wallet. Contract bytecode is bundled in this page; no Highlight service, relayer, or deploy key is involved.</p>
        </div>
        <button className="wallet deploy-wallet" onClick={connect}>{shortAccount}</button>
      </header>

      <section className="deploy-safety" aria-label="Deployment preflight">
        <div><span>Connected</span><strong>{account ? shorten(account) : "No wallet"}</strong></div>
        <div><span>Collection owner</span><strong>{collectionOwner ? shorten(collectionOwner) : "Reading chain…"}</strong></div>
        <div><span>Owner match</span><strong className={ownerMatches ? "good" : "warn"}>{ownerMatches ? "Verified" : "Required for every step"}</strong></div>
        <div><span>Recovery state</span><strong>{Number(legacyRecovered[230]) + Number(legacyRecovered[231])} / 2 complete</strong></div>
      </section>

      {status.message && (
        <div className={`deploy-status transaction ${status.kind}`} role="status" aria-live="polite">
          {status.message}
          {status.hash && (
            <a href={`${status.chain === "base" ? "https://basescan.org" : "https://etherscan.io"}/tx/${status.hash}`} target="_blank" rel="noreferrer">View transaction ↗</a>
          )}
        </div>
      )}

      <section className="deploy-steps">
        <article className={`deploy-step ${config.burnerAddress ? "done" : ""}`}>
          <div className="deploy-step-number">01</div>
          <div className="deploy-step-body">
            <p className="eyebrow">Base mainnet</p>
            <h2>Deploy burner</h2>
            <p>The burner accepts token ID 1 from the existing 1988 collection and emits a verifiable redemption for Ethereum.</p>
            <dl className="deploy-arguments">
              <div><dt>Contract owner</dt><dd>{collectionOwner ? shorten(collectionOwner) : "Reading chain…"}</dd></div>
              <div><dt>1988 contract</dt><dd>{shorten(BURN_TOKEN)}</dd></div>
              <div><dt>Token ID</dt><dd>1</dd></div>
              <div><dt>Starting redemption</dt><dd>{LEGACY_REDEMPTION_COUNT.toString()}</dd></div>
            </dl>
            <button className="primary" onClick={deployBurner} disabled={Boolean(config.burnerAddress)}>{config.burnerAddress ? "Burner deployed" : "Deploy on Base with Ledger"}</button>
            <AddressResult label="Burner" value={config.burnerAddress} explorer="https://basescan.org/address/" />
          </div>
        </article>

        <article className={`deploy-step ${config.receiverAddress ? "done" : ""}`}>
          <div className="deploy-step-number">02</div>
          <div className="deploy-step-body">
            <p className="eyebrow">Ethereum mainnet</p>
            <h2>Deploy receiver</h2>
            <p>The receiver verifies claims against the Base burner. In Ledger-only mode, minting requires the owner’s explicit approval for every redemption.</p>
            <dl className="deploy-arguments">
              <div><dt>Hardware collection</dt><dd>{shorten(HARDWARE_COLLECTION)}</dd></div>
              <div><dt>Source chain</dt><dd>Base / 8453</dd></div>
              <div><dt>Mint authority</dt><dd>Ledger owner only</dd></div>
              <div><dt>Relay path</dt><dd>Not in bytecode</dd></div>
            </dl>
            <button className="primary" onClick={deployReceiver} disabled={!config.burnerAddress || Boolean(config.receiverAddress)}>{config.receiverAddress ? "Receiver deployed" : "Deploy on Ethereum with Ledger"}</button>
            <AddressResult label="Receiver" value={config.receiverAddress} explorer="https://etherscan.io/address/" />
          </div>
        </article>

        <article className={`deploy-step ${receiverRegistered ? "done" : ""}`}>
          <div className="deploy-step-number">03</div>
          <div className="deploy-step-body">
            <p className="eyebrow">Existing Hardware collection</p>
            <h2>Activate minter</h2>
            <p>Authorize the deployed receiver for the two recoveries. The Highlight minter remains temporarily authorized until the final cutover in Step 06.</p>
            {!ownerMatches && <p className="deploy-warning">Step 03 must be signed by the current collection owner, {collectionOwner ?? "which is still loading"}.</p>}
            <button className="primary" onClick={registerMinter} disabled={!config.receiverAddress || receiverRegistered}>{receiverRegistered ? "Receiver registered" : "Register receiver with Ledger"}</button>
            <AddressResult label="Authorized receiver" value={receiverRegistered ? config.receiverAddress : undefined} explorer="https://etherscan.io/address/" />
          </div>
        </article>

        <article className={`deploy-step ${recoveryComplete ? "done" : ""}`}>
          <div className="deploy-step-number">04</div>
          <div className="deploy-step-body">
            <p className="eyebrow">Verified legacy burns</p>
            <h2>Mint #230 + #231</h2>
            <p>The receiver contains exactly these two historical make-goods. Their recipients are fixed in the bytecode, each can mint once, and #230 must complete before #231.</p>
            {!ownerMatches && <p className="deploy-warning">Both recoveries must be signed by the current collection owner, {collectionOwner ?? "which is still loading"}.</p>}
            <div className="legacy-recovery-list">
              {LEGACY_RECOVERIES.map((recovery) => {
                const number = Number(recovery.redemptionNumber) as 230 | 231;
                const recovered = legacyRecovered[number];
                const waitingFor230 = number === 231 && !legacyRecovered[230];
                return (
                  <div className="legacy-recovery" key={number}>
                    <div>
                      <strong>Hardware #{number}</strong>
                      <span>{recovery.recipient}</span>
                      <a href={`https://basescan.org/tx/${recovery.burnTx}`} target="_blank" rel="noreferrer">Verified Base burn ↗</a>
                    </div>
                    <button className="primary" onClick={() => recoverLegacy(number)} disabled={!ownerMatches || !receiverRegistered || recovered || waitingFor230}>
                      {recovered ? "Recovered" : waitingFor230 ? "Recover #230 first" : `Mint #${number} with Ledger`}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </article>

        <article className={`deploy-step ${metadataMigrated ? "done" : ""}`}>
          <div className="deploy-step-number">05</div>
          <div className="deploy-step-body">
            <p className="eyebrow">Collection metadata</p>
            <h2>Leave Highlight infrastructure</h2>
            <p>Move the entire collection’s metadata base to hardware.jack.art. All 229 existing records were preserved, #229’s blank preview is repaired, and every image and live artwork URL resolves to the permanent Arweave archive.</p>
            <div className="repair-preview">
              <img src={TOKEN_229_IMAGE} alt="Correct Hardware #229 artwork" />
              <dl className="deploy-arguments">
                <div><dt>Metadata base</dt><dd>hardware.jack.art</dd></div>
                <div><dt>Existing records</dt><dd>229 / 229 archived</dd></div>
                <div><dt>Artwork source</dt><dd>Permanent Arweave</dd></div>
                <div><dt>Endpoint check</dt><dd>{metadataArchiveReady ? "Verified" : recoveryComplete ? "Checked before signing" : "After recovery"}</dd></div>
              </dl>
            </div>
            {!ownerMatches && <p className="deploy-warning">This cutover must be signed by the current collection owner, {collectionOwner ?? "which is still loading"}.</p>}
            <button className="primary" onClick={migrateMetadata} disabled={!ownerMatches || metadataMigrated || !recoveryComplete}>
              {metadataMigrated ? "Metadata migrated" : !recoveryComplete ? "Recover #230 + #231 first" : "Migrate metadata with Ledger"}
            </button>
          </div>
        </article>

        <article className={`deploy-step ${legacyMinterRetired ? "done" : ""}`}>
          <div className="deploy-step-number">06</div>
          <div className="deploy-step-body">
            <p className="eyebrow">Final Ethereum cutover</p>
            <h2>Retire Highlight’s minter</h2>
            <p>Remove the old Highlight contract only after both missing tokens are recovered and the collection metadata has moved. The new receiver then becomes the sole authorized Hardware minter.</p>
            <dl className="deploy-arguments">
              <div><dt>Legacy minter</dt><dd>{shorten(LEGACY_MINTER)}</dd></div>
              <div><dt>Status</dt><dd>{collectionLoaded ? legacyMinterRegistered ? "Authorized" : "Removed" : "Reading chain…"}</dd></div>
            </dl>
            {!ownerMatches && <p className="deploy-warning">This removal must be signed by the current collection owner, {collectionOwner ?? "which is still loading"}.</p>}
            <button
              className="primary"
              onClick={retireLegacyMinter}
              disabled={!collectionLoaded || !legacyMinterRegistered || !ownerMatches || !receiverRegistered || !recoveryComplete || !metadataMigrated}
            >
              {!collectionLoaded ? "Checking current minters…" : legacyMinterRetired ? "Highlight minter removed" : "Remove Highlight minter with Ledger"}
            </button>
          </div>
        </article>
      </section>

      <footer className="deploy-footer">
        <div>
          <p className="eyebrow">Deployment record</p>
          <h2>{complete ? "Ready for claims." : "Complete the six steps above."}</h2>
          <p>The public site discovers the registered receiver and its Base burner directly on-chain. Download this configuration as a permanent deployment record.</p>
        </div>
        <button className="secondary" onClick={exportConfig} disabled={!config.burnerAddress && !config.receiverAddress}>Download deployment config</button>
      </footer>
    </main>
  );
}

function shorten(value: string) {
  return `${value.slice(0, 8)}…${value.slice(-6)}`;
}

function AddressResult({ label, value, explorer }: { label: string; value?: string; explorer: string }) {
  return (
    <div className="deploy-result">
      <span>{label}</span>
      {value
        ? <a href={`${explorer}${value}`} target="_blank" rel="noreferrer">{shorten(value)} ↗</a>
        : <strong>Awaiting deployment</strong>}
    </div>
  );
}
